import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import {
  type Comparison,
  compareTo,
  dayLabel,
  RANGE,
  type SeriesPoint,
  shiftDay,
  WEEK,
} from '../lib/radar-trends';
import { AdminTrendChart } from './AdminTrendChart';
import { AdminTrendDelta } from './AdminTrendDelta';

// The selected card's panel, under its band's grid (#6715): "<Band> · <Label>", the
// window, the change against seven days back and against thirty, then the chart.
// Each change shows only when its exact day is stored — a tier younger than thirty
// days has no "in 30 days" figure rather than one measured from a later day. Fewer
// than two points (today's live value alone) is not a line: it says so instead.

export function AdminTrendPanel(props: {
  title: string;
  series: readonly SeriesPoint[];
  week: Comparison;
  flow: boolean;
  inverse: boolean;
  today: string;
}): JSX.Element {
  const month = () => compareTo(props.series, RANGE, props.inverse);
  const delta = (c: Comparison) => (c.kind === 'delta' ? c : null);
  return (
    <div class="card admin-trend-panel">
      <div class="admin-trend-panel__head">
        <p class="admin-trend-panel__title">
          <span class="metric-grid__label">{props.title}</span>
          <span class="admin-trend__muted">{t('admin.trend_last_30')}</span>
        </p>
        <p class="admin-trend-panel__deltas">
          <Show when={delta(props.week)}>
            {(w) => (
              <span class="admin-trend__change">
                <AdminTrendDelta delta={w().delta} tone={w().tone} />
                <span class="admin-trend__muted">
                  {t('admin.trend_vs', {
                    date: dayLabel(shiftDay(props.today, -WEEK), props.today),
                  })}
                </span>
              </span>
            )}
          </Show>
          <Show when={delta(month())}>
            {(m) => (
              <span class="admin-trend__change">
                <AdminTrendDelta delta={m().delta} tone={m().tone} />
                <span class="admin-trend__muted">{t('admin.trend_in_30')}</span>
              </span>
            )}
          </Show>
        </p>
      </div>
      <Show
        when={props.series.length >= 2}
        fallback={<p class="admin-trend__muted">{t('admin.trend_no_history')}</p>}
      >
        <AdminTrendChart
          title={props.title}
          series={props.series}
          flow={props.flow}
          inverse={props.inverse}
          tone={props.week.kind === 'delta' ? props.week.tone : 'flat'}
          today={props.today}
        />
      </Show>
    </div>
  );
}
